import React from "react";
import { useNavigate } from "react-router-dom";

const Projects = () => {
  const navigate = useNavigate();
  
  const projects = [
    {
      id: "my-protocol",
      title: "My Protocol",
      description: "Premium fitness and nutrition tracking app for serious trainees—designed for clarity, accuracy, and insight over gamification.",
      image: "https://customer-assets.emergentagent.com/job_1def07b2-6293-4c22-9598-3b0db649a536/artifacts/4fj0qgwx_My%20Protocol%20Icon.png",
      tags: ["Mobile App", "iOS/Android", "React Native", "Product Design"],
      internalLink: "/case-studies/my-protocol"
    },
    {
      id: "bx-kicks",
      title: "BX Kicks",
      description: "Premium sneaker e-commerce platform built for the Bronx streetwear community with mobile-first design and authentic brand identity.",
      image: "https://customer-assets.emergentagent.com/job_uxdev-portfolio/artifacts/7tchd93b_BX%20KICKS%20%281%29.PNG",
      tags: ["E-commerce", "Shopify", "Branding", "Mobile-First"],
      internalLink: "/case-studies/bx-kicks"
    },
    {
      id: "koniag-government-services",
      title: "Koniag Government Services",
      description: "Created a comprehensive digital platform to streamline government services and improve citizen engagement through intuitive design.",
      image: "https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/4fdld10a_Group%20277.png",
      tags: ["Web Platform", "Government", "Accessibility"],
      internalLink: "/case-studies/koniag-government-services"
    },
    {
      id: "fitness-by-artizen",
      title: "Fitness by Artizen",
      description:
        "Designed a calm, premium, conversion-focused membership, trial, and consultation experience for a boutique strength club in NYC.",
      image: "https://customer-assets.emergentagent.com/job_ux-design-revamp/artifacts/vjlao53w_PHOTO-2026-01-06-16-28-08.jpg",
      tags: ["Membership Systems", "Conversion Architecture", "Fitness"],
      internalLink: "/case-studies/fitness-by-artizen"
    },
    {
      id: "scite",
      title: "scite_",
      description: "Designed an innovative research platform that helps academics and researchers discover and cite reliable scientific literature.",
      image: "https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/2l1uupvt_scite-opengraph.jpg",
      tags: ["Research Platform", "Data Visualization", "Academia"],
      internalLink: "/case-studies/scite"
    },
    {
      id: "24-hour-fitness",
      title: "24 Hour Fitness",
      description: "Redesigned the mobile app experience to improve user engagement and streamline workout tracking for fitness enthusiasts.",
      image: "https://customer-assets.emergentagent.com/job_pro-webfolio-2/artifacts/jxcd867x_24%20Hero%20%282%29.jpg",
      tags: ["Mobile App", "User Research", "Prototyping"],
      internalLink: "/case-studies/24-hour-fitness"
    },
  ];

  return (
    <section id="projects" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Featured Work
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A selection of projects showcasing user-centered design solutions 
            across various industries and platforms.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <div
              key={project.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              data-testid={`project-${project.id}`}
            >
              <div className="relative overflow-hidden">
                {project.image ? (
                  <img
                    src={project.image}
                    alt={project.title}
                    className={`w-full transition-transform duration-300 group-hover:scale-105 ${
                      project.id === "koniag-government-services" 
                        ? "h-48 object-contain bg-gray-50" 
                        : project.id === "my-protocol"
                        ? "h-64 object-contain bg-slate-900"
                        : "h-64 object-cover"
                    }`}
                    loading="lazy"
                  />
                ) : (
                  <div className={`w-full h-64 bg-gradient-to-br ${project.gradient || 'from-gray-400 to-gray-600'} flex items-center justify-center transition-transform duration-300 group-hover:scale-105`}>
                    {project.icon === "chart" && (
                      <svg className="w-20 h-20 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    )}
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {project.title}
                </h3>
                
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {project.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                      data-testid={`project-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                <button
                  onClick={() => navigate(project.internalLink)}
                  className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors group"
                  data-testid={`project-link-${project.id}`}
                >
                  View Case Study
                  <svg
                    className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <p className="text-lg text-gray-600 mb-8">
            Interested in working together or learning more about my process?
          </p>
          <div className="space-y-4">
            <p className="text-gray-700 font-medium text-lg">
              Let&apos;s create something amazing together.
            </p>
            <p className="text-sm text-gray-500">
              New York City • Available for freelance and full-time opportunities
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;